import React, { useMemo } from "react";
import { Location, Trip, TransportData, TripTransport } from "@/types/trip";
import { ServerFlightInfo } from "@/types/flights";
import FlightContainer from "./FlightContainer";
import ComingSoonTransport from "./ComingSoonTransport";
import TopBar from "./TransportTopBar";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import TransportForm from "./customTransport";
import useTripStore from "@/stores/tripBuilder/useTrip";

interface TransportContainerProps {
  transportData: {
    selectedLocation: Location | null;
    locationAbove: Location | null;
    locationBelow: Location | null;
  };
  selectedFlight: TripTransport | null;
  setSelectedFlight: (flight: TripTransport | null) => void;
  addFlightToTrip: (
    trip: Trip,
    flight: ServerFlightInfo,
    transportData: {
      selectedLocation: Location | null;
      locationAbove: Location | null;
      locationBelow: Location | null;
    }
  ) => void;
  flightModalOpen: boolean;
  setFlightModalOpen: (isOpen: boolean) => void;
}

const TransportContainer: React.FC<TransportContainerProps> = ({
  transportData,
  selectedFlight,
  setSelectedFlight,
  addFlightToTrip,
  flightModalOpen,
  setFlightModalOpen,
}) => {
  const { activeTransportMode } = useTransportStore();
  const { tripData, updateTripData } = useTripStore();
  const { checkIsAdmin } = useTripStore();
  const isAdmin = useMemo(() => checkIsAdmin(), [checkIsAdmin]);

  const handleCustomTransportSubmit = (newTransport: TripTransport) => {
    if (!tripData) return;

    const updatedTrip = { ...tripData };

    // Check if transport already exists for this route
    const existingTransportIndex = updatedTrip.transport.findIndex(
      (t) =>
        t.fromLocationId === newTransport.fromLocationId &&
        t.toLocationId === newTransport.toLocationId
    );

    if (existingTransportIndex !== -1) {
      // Replace existing transport
      updatedTrip.transport[existingTransportIndex] = newTransport;
    } else {
      // Add new transport
      updatedTrip.transport.push(newTransport);
    }

    updateTripData(updatedTrip);
    setFlightModalOpen(false);
  };

  const renderTransportContent = () => {
    let customTransportData: TransportData | undefined;

    switch (activeTransportMode) {
      case "flights":
        return (
          <FlightContainer
            transportData={transportData}
            selectedFlight={selectedFlight}
            setSelectedFlight={setSelectedFlight}
            addFlightToTrip={addFlightToTrip}
            flightModalOpen={flightModalOpen}
            setFlightModalOpen={setFlightModalOpen}
          />
        );
      case "trains":
        return <ComingSoonTransport mode="trains" />;
      case "buses":
        return <ComingSoonTransport mode="buses" />;
      case "custom":
        if (
          !transportData.locationAbove?.name ||
          !transportData.locationBelow?.name ||
          !tripData
        ) {
          return null;
        }
        customTransportData = {
          selectedLocation: transportData.selectedLocation,
          locationAbove: transportData.locationAbove,
          locationBelow: transportData.locationBelow,
        };
        return (
          <TransportForm
            transportData={customTransportData}
            tripData={tripData}
            onSubmit={handleCustomTransportSubmit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full h-[calc(100vh-1.8rem)] flex flex-col items-center justify-start mt-2">
      <TopBar isAdmin={isAdmin} />
      <div className="w-full flex-1 mt-4 overflow-hidden rounded-lg">
        {renderTransportContent()}
      </div>
    </div>
  );
};

export default TransportContainer;
